import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Loader from '@amzn/meridian/loader';
import Alert from '@amzn/meridian/alert';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import { adminApi, DepartmentRecord } from '../service/adminApiService';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useToast } from '../components/ToastProvider';
import { PageHeader } from '../components/PageHeader';

export const DepartmentsListPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [departments, setDepartments] = useState<DepartmentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<DepartmentRecord | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await adminApi.listDepartments();
      setDepartments(resp.departments);
    } catch (err: any) {
      setError(err?.message || 'Failed to load departments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const performDelete = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      await adminApi.deleteDepartment(pending.department_name);
      toast.push({ type: 'success', message: `Deleted department ${pending.department_name}` });
      setPending(null);
      refresh();
    } catch (err: any) {
      toast.push({ type: 'error', message: err?.message || 'Delete failed' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Column spacing="large">
      <PageHeader
        title="Departments"
        subtitle="Departments populate the group form's dropdown and attach tenant metadata."
        actions={
          <Button type="primary" onClick={() => navigate('/admin/departments/new')}>
            + New department
          </Button>
        }
      />

      {error && <Alert type="error">{error}</Alert>}

      {loading ? (
        <Row alignmentHorizontal="center" spacingInset="500">
          <Loader type="circular" size="large" />
        </Row>
      ) : departments.length === 0 ? (
        <Alert type="neutral">No departments defined.</Alert>
      ) : (
        <Table headerRows={1} showDividers={true} aria-label="Departments">
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Display name</TableCell>
            <TableCell>Contact email</TableCell>
            <TableCell>Last updated</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
          {departments.map((d) => (
            <TableRow key={d.department_name}>
              <TableCell>
                <Link to={`/admin/departments/${encodeURIComponent(d.department_name)}/edit`}>
                  {d.department_name}
                </Link>
              </TableCell>
              <TableCell>
                <Text type="b200">{d.display_name || '-'}</Text>
              </TableCell>
              <TableCell>
                <Text type="b200">{d.contact_email || '-'}</Text>
              </TableCell>
              <TableCell>
                <Text type="b200">{d.last_updated?.slice(0, 19) || '-'}</Text>
              </TableCell>
              <TableCell>
                <Row spacing="xsmall">
                  <Button
                    type="tertiary"
                    size="small"
                    onClick={() =>
                      navigate(`/admin/departments/${encodeURIComponent(d.department_name)}/edit`)
                    }
                  >
                    Edit
                  </Button>
                  <Button type="tertiary" size="small" onClick={() => setPending(d)}>
                    Delete
                  </Button>
                </Row>
              </TableCell>
            </TableRow>
          ))}
        </Table>
      )}

      <ConfirmDialog
        open={!!pending}
        title={`Delete department ${pending?.department_name || ''}?`}
        message="The backend will reject this if any policy still references the department."
        confirmLabel="Delete"
        destructive
        busy={busy}
        onConfirm={performDelete}
        onCancel={() => setPending(null)}
      />
    </Column>
  );
};
